import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { AuthStoreService } from '@railmybox/auth';
import { from, Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { translate } from '@jsverse/transloco';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  protected authStoreService = inject(AuthStoreService);
  protected router = inject(Router);
  private messageService = inject(MessageService);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse && error.error instanceof Blob) {
          return from(this.handleBlobError(error)).pipe(switchMap((handledError) => throwError(() => handledError)));
        }
        this.showFallbackToast(error);
        // pass it to the component or service that initiated the request
        return throwError(() => error);
      })
    );
  }

  /**
   * File requests use responseType 'blob', so Angular delivers their error body as a Blob as well,
   * which components cannot read. Their problem message is therefore always shown here as a toast.
   * @returns the error with the parsed problem body, or the original error if the body is no JSON
   */
  private async handleBlobError(error: HttpErrorResponse): Promise<HttpErrorResponse> {
    let problem: { detail?: string; message?: string };
    try {
      problem = JSON.parse(await error.error.text());
    } catch {
      // not a problem response (e.g. an HTML error page of a proxy), treat it like any other error
      this.showFallbackToast(error);
      return error;
    }
    this.showToast(problem?.detail || problem?.message);
    return new HttpErrorResponse({
      error: problem,
      headers: error.headers,
      status: error.status,
      statusText: error.statusText,
      url: error.url ?? undefined,
    });
  }

  private showFallbackToast(error: HttpErrorResponse): void {
    if (error instanceof HttpErrorResponse && this.router.routerState.snapshot.url.includes('support')) {
      if (error.status === 400 || error.status >= 500) {
        this.showToast(error.error?.detail || error.error?.message);
      }
    }
    if (error.status === 412) {
      this.showToast(translate('eTagMessage'));
    }
  }

  private showToast(detail: string): void {
    this.messageService.add({ severity: 'error', detail, life: 30000 });
  }
}
